import {UploadButton} from "@components";
import {ProfileIcon, SaveIcon} from "@components/icons";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {translate} from "@util";
import {useEffect, useState} from "react";

export type ProfileData = {
  name?: string;
  surname?: string;
  email?: string;
  phone?: string;
  img?: string;
};

const ProfileDialog = ({
  open,
  data: dataProp,
  loading,
  onSave,
  onClose,
}: {
  open: boolean;
  data: ProfileData;
  loading: boolean;
  onClose: () => void;
  onSave: (data: ProfileData) => void;
}) => {
  const {theme} = useThemeContext();

  const [data, setData] = useState<ProfileData>({});

  useEffect(() => {
    setData(dataProp);
  }, [dataProp]);

  const handleChange = (e: {
    target: {name: string; value: string | undefined};
  }) => {
    setData(old => {
      return {
        ...old,
        [e.target.name]: e.target.value,
      };
    });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      onClick={e => e.stopPropagation()}>
      <Stack direction="row" mt={2} justifyContent="center">
        <Box
          sx={{
            bgcolor: theme.palette.secondary.dark,
            width: "64px",
            borderRadius: "32px",
            height: "64px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <ProfileIcon
            fontSize="large"
            sx={{
              color: theme.palette.getContrastText(
                theme.palette.secondary.dark,
              ),
            }}
          />
        </Box>
      </Stack>
      <DialogTitle align="center">
        {translate("pages.home.profile.title")}
      </DialogTitle>
      <DialogContent>
        <Stack direction={"column"} gap={2}>
          <TextField
            tabIndex={90}
            label={translate("entity.user.name")}
            variant="standard"
            name="name"
            value={data.name ?? ""}
            onChange={handleChange}
          />
          <TextField
            tabIndex={91}
            label={translate("entity.user.surname")}
            variant="standard"
            name="surname"
            value={data.surname ?? ""}
            onChange={handleChange}
          />
          <TextField
            tabIndex={92}
            label={translate("entity.user.email")}
            variant="standard"
            name="email"
            value={data.email ?? ""}
            onChange={handleChange}
          />
          <TextField
            tabIndex={93}
            label={translate("entity.user.phone")}
            variant="standard"
            name="phone"
            type="tel"
            value={data.phone ?? ""}
            onChange={handleChange}
          />

          <Stack direction="column" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {translate("entity.user.avatar")}
            </Typography>
            <Stack direction="row" gap={1}>
              <UploadButton
                label={translate("entity.user.img")}
                sx={{
                  "& .upload-button-text-input": {display: "none"},
                }}
                name="img"
                fileId={data.img}
                fileType="image"
                onChange={base64 =>
                  handleChange({target: {name: "img", value: base64}})
                }
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate("common.action.close")}</Button>
        <Button
          loading={loading}
          onClick={() => onSave(data)}
          startIcon={<SaveIcon />}
          variant="contained">
          {translate("common.action.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export {ProfileDialog};
